<template>
  <div id="layout-wrapper">
    <NavigationBar />
    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">
          <!-- Page Header -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Attendance Clock</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="javascript:void(0)" @click="gotTo('employee-dashboard')">Dashboard</a></li>
                    <li class="breadcrumb-item active">Clock</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center p-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading clock status...</p>
          </div>

          <div v-else-if="clockData">
            <!-- Current Time Display -->
            <div class="row">
              <div class="col-12">
                <div class="card bg-primary bg-gradient text-white">
                  <div class="card-body text-center py-5">
                    <h1 class="display-1 mb-0 fw-bold text-white">{{ currentTime }}</h1>
                    <h5 class="text-white-50 mt-2">{{ formatDateLong(currentDate) }}</h5>
                    <div class="mt-3">
                      <span class="badge badge-soft-light font-size-14">
                        <i class="mdi mdi-calendar me-1"></i>
                        {{ clockData.employee.department }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Clock Status & Action -->
            <div class="row">
              <!-- Current Status Card -->
              <div class="col-md-6">
                <div class="card">
                  <div class="card-body">
                    <div class="text-center">
                      <div class="avatar-xl mx-auto mb-4">
                        <div class="avatar-title rounded-circle"
                             :class="clockData.clockStatus.isClockedIn ? 'bg-success' : 'bg-danger'">
                          <i class="mdi font-size-48"
                             :class="clockData.clockStatus.isClockedIn ? 'mdi-clock-check' : 'mdi-clock-outline'"></i>
                        </div>
                      </div>
                      <h4 class="mb-3">
                        <span v-if="clockData.clockStatus.isClockedIn" class="text-success">
                          <i class="mdi mdi-circle-medium"></i>
                          Currently Clocked In
                        </span>
                        <span v-else class="text-danger">
                          <i class="mdi mdi-circle-medium"></i>
                          Currently Clocked Out
                        </span>
                      </h4>

                      <!-- Check In Time -->
                      <div v-if="clockData.clockStatus.checkInTime" class="mb-3">
                        <p class="text-muted mb-1">Check In Time</p>
                        <h5 class="text-success">
                          <i class="mdi mdi-clock-in me-1"></i>
                          {{ formatTime(clockData.clockStatus.checkInTime) }}
                        </h5>
                      </div>

                      <!-- Check Out Time -->
                      <div v-if="clockData.clockStatus.checkOutTime" class="mb-3">
                        <p class="text-muted mb-1">Check Out Time</p>
                        <h5 class="text-danger">
                          <i class="mdi mdi-clock-out me-1"></i>
                          {{ formatTime(clockData.clockStatus.checkOutTime) }}
                        </h5>
                      </div>

                      <!-- Working Duration -->
                      <div v-if="clockData.clockStatus.isClockedIn && clockData.clockStatus.checkInTime" class="mb-3">
                        <p class="text-muted mb-1">Working Duration</p>
                        <h3 class="text-primary mb-0">{{ workingDuration }}</h3>
                      </div>

                      <!-- Total Hours (if clocked out) -->
                      <div v-if="!clockData.clockStatus.isClockedIn && clockData.clockStatus.totalHours > 0" class="mb-3">
                        <p class="text-muted mb-1">Total Hours Today</p>
                        <h3 class="text-primary mb-0">{{ clockData.clockStatus.totalHours }}h</h3>
                      </div>

                      <!-- Clock Action Button -->
                      <div class="mt-4">
                        <button
                            v-if="clockData.clockStatus.isClockedIn"
                            @click="openClockModal('OUT')"
                            class="btn btn-danger btn-lg w-100"
                            :disabled="processing">
                          <i class="mdi mdi-clock-out me-2"></i>
                          <span v-if="processing">Processing...</span>
                          <span v-else>Clock Out</span>
                        </button>
                        <button
                            v-else
                            @click="openClockModal('IN')"
                            class="btn btn-success btn-lg w-100"
                            :disabled="processing">
                          <i class="mdi mdi-clock-in me-2"></i>
                          <span v-if="processing">Processing...</span>
                          <span v-else>Clock In</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Work Schedule & Stats -->
              <div class="col-md-6">
                <!-- Work Schedule -->
                <div class="card">
                  <div class="card-header">
                    <h6 class="card-title mb-0">
                      <i class="mdi mdi-clock-outline text-primary me-2"></i>
                      Today's Schedule
                    </h6>
                  </div>
                  <div class="card-body">
                    <div class="d-flex justify-content-between mb-3">
                      <div>
                        <p class="text-muted mb-1">Start Time</p>
                        <h5 class="mb-0">{{ formatScheduleTime(clockData.schedule.startTime) }}</h5>
                      </div>
                      <div class="text-end">
                        <p class="text-muted mb-1">End Time</p>
                        <h5 class="mb-0">{{ formatScheduleTime(clockData.schedule.endTime) }}</h5>
                      </div>
                    </div>
                    <div class="text-center">
                      <p class="text-muted mb-1">Break Duration</p>
                      <h5 class="mb-0">{{ clockData.schedule.breakMinutes }} minutes</h5>
                    </div>
                  </div>
                </div>

                <!-- Month Statistics -->
                <div class="card">
                  <div class="card-header">
                    <h6 class="card-title mb-0">
                      <i class="mdi mdi-chart-box text-primary me-2"></i>
                      This Month
                    </h6>
                  </div>
                  <div class="card-body">
                    <div class="row text-center">
                      <div class="col-6 mb-3">
                        <p class="text-muted mb-1 small">Days Present</p>
                        <h4 class="mb-0 text-success">{{ clockData.monthStats.totalDays }}</h4>
                      </div>
                      <div class="col-6 mb-3">
                        <p class="text-muted mb-1 small">Total Hours</p>
                        <h4 class="mb-0 text-primary">{{ clockData.monthStats.totalHours }}h</h4>
                      </div>
                      <div class="col-6">
                        <p class="text-muted mb-1 small">Late Days</p>
                        <h4 class="mb-0 text-warning">{{ clockData.monthStats.lateDays }}</h4>
                      </div>
                      <div class="col-6">
                        <p class="text-muted mb-1 small">Overtime</p>
                        <h4 class="mb-0 text-info">{{ clockData.monthStats.totalOvertime }}h</h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Today's Attendance Logs -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-header">
                    <h6 class="card-title mb-0">
                      <i class="mdi mdi-history text-primary me-2"></i>
                      Today's Activity Log
                    </h6>
                  </div>
                  <div class="card-body">
                    <div v-if="clockData.todayLogs.length === 0" class="text-center text-muted py-4">
                      <i class="mdi mdi-clock-outline font-size-48"></i>
                      <p class="mt-2 mb-0">No activity recorded today</p>
                    </div>
                    <div v-else class="timeline">
                      <div v-for="(log, index) in clockData.todayLogs" :key="index" class="timeline-item">
                        <div class="timeline-badge" :class="log.action === 'IN' || log.action === 'ENTER' ? 'bg-success' : 'bg-danger'">
                          <i class="mdi" :class="log.action === 'IN' || log.action === 'ENTER' ? 'mdi-login' : 'mdi-logout'"></i>
                        </div>
                        <div class="timeline-panel">
                          <div class="timeline-heading">
                            <h6 class="timeline-title">
                              {{ log.action === 'IN' || log.action === 'ENTER' ? 'Clocked In' : 'Clocked Out' }}
                              <span v-if="log.location_type" class="ms-2">
                                <span v-if="log.location_type === 'Office'" class="badge bg-success-subtle text-success">
                                  <i class="mdi mdi-office-building me-1"></i>Office
                                </span>
                                <span v-else-if="log.location_type === 'Remote'" class="badge bg-info-subtle text-info">
                                  <i class="mdi mdi-home me-1"></i>Remote
                                </span>
                                <span v-else class="badge bg-warning-subtle text-warning">
                                  <i class="mdi mdi-map-marker-alert me-1"></i>Outside
                                </span>
                              </span>
                            </h6>
                          </div>
                          <div class="timeline-body">
                            <p class="text-muted mb-1">
                              <i class="mdi mdi-clock-outline me-1"></i>
                              {{ formatTime(log.timestamp) }}
                            </p>
                            <p v-if="log.location" class="text-muted mb-1">
                              <i class="mdi mdi-map-marker me-1"></i>
                              {{ log.location }}
                            </p>
                            <p v-if="log.location_name && log.location_type === 'Office'" class="text-muted mb-1">
                              <i class="mdi mdi-office-building me-1"></i>
                              <strong>{{ log.location_name }}</strong>
                            </p>
                            <p v-if="log.distance_from_office" class="text-muted mb-1">
                              <i class="mdi mdi-map-marker-distance me-1"></i>
                              {{ formatDistance(log.distance_from_office) }} from office
                            </p>
                            <p v-if="log.notes" class="text-muted mb-0">
                              <i class="mdi mdi-note-text me-1"></i>
                              {{ log.notes }}
                            </p>
                            <div v-if="log.latitude && log.longitude" class="mt-2">
                              <small class="text-muted">
                                <i class="mdi mdi-crosshairs-gps me-1"></i>
                                {{ log.latitude }}, {{ log.longitude }}
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Recent Attendance -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-header d-flex justify-content-between align-items-center">
                    <h6 class="card-title mb-0">
                      <i class="mdi mdi-calendar-week text-primary me-2"></i>
                      Last 7 Days
                    </h6>
                    <button @click="gotTo('employee-attendance-records')" class="btn btn-sm btn-outline-primary">
                      <i class="mdi mdi-arrow-right me-1"></i>
                      View All
                    </button>
                  </div>
                  <div class="card-body">
                    <div v-if="clockData.recentAttendance.length === 0" class="text-center text-muted py-4">
                      No recent attendance records
                    </div>
                    <div v-else class="table-responsive">
                      <table class="table table-sm mb-0">
                        <thead class="table-light">
                        <tr>
                          <th>Date</th>
                          <th>Check In</th>
                          <th>Check Out</th>
                          <th class="text-center">Hours</th>
                          <th>Status</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="record in clockData.recentAttendance" :key="record.id">
                          <td>
                            <strong>{{ formatDate(record.date) }}</strong>
                          </td>
                          <td>
                              <span v-if="record.first_in" class="text-success">
                                <i class="mdi mdi-clock-in"></i> {{ record.first_in }}
                              </span>
                            <span v-else class="text-muted">-</span>
                          </td>
                          <td>
                              <span v-if="record.last_out" class="text-danger">
                                <i class="mdi mdi-clock-out"></i> {{ record.last_out }}
                              </span>
                            <span v-else class="text-muted">-</span>
                          </td>
                          <td class="text-center">
                            <span class="badge bg-primary">{{ record.total_hours || '0' }}h</span>
                          </td>
                          <td>
                              <span :class="getStatusBadgeClass(record.status)">
                                {{ record.status }}
                              </span>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

        </div>
      </div>
    </div>

    <!-- Clock Action Modal -->
    <div class="modal fade" id="clockActionModal" tabindex="-1" ref="clockActionModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="mdi me-2" :class="clockAction === 'IN' ? 'mdi-clock-in' : 'mdi-clock-out'"></i>
              {{ clockAction === 'IN' ? 'Clock In' : 'Clock Out' }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <div class="text-center mb-4">
              <div class="avatar-lg mx-auto mb-3">
                <div class="avatar-title rounded-circle"
                     :class="clockAction === 'IN' ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'">
                  <i class="mdi font-size-36"
                     :class="clockAction === 'IN' ? 'mdi-clock-in' : 'mdi-clock-out'"></i>
                </div>
              </div>
              <h5>{{ clockAction === 'IN' ? 'Ready to start your day?' : 'Finished for the day?' }}</h5>
              <p class="text-muted">{{ currentTime }}</p>
            </div>

            <!-- Location Capture -->
            <div class="mb-3">
              <label class="form-label">
                Location <span class="text-danger">*</span>
                <small class="text-muted">(Required - Allow browser location access)</small>
              </label>

              <div v-if="gettingLocation" class="alert alert-info">
                <div class="d-flex align-items-center">
                  <div class="spinner-border spinner-border-sm me-2" role="status"></div>
                  <span>Getting your location...</span>
                </div>
              </div>

              <div v-else-if="locationError" class="alert alert-danger">
                <i class="mdi mdi-alert-circle me-1"></i>
                {{ locationError }}
                <button @click="getCurrentLocation" class="btn btn-sm btn-danger mt-2 w-100">
                  <i class="mdi mdi-refresh me-1"></i>
                  Try Again
                </button>
              </div>

              <div v-else-if="clockForm.address" class="alert alert-success">
                <div class="d-flex justify-content-between align-items-start">
                  <div>
                    <i class="mdi mdi-map-marker-check me-1"></i>
                    <strong>Location Captured</strong>
                    <p class="mb-1 mt-2">{{ clockForm.address }}</p>
                    <small class="text-muted">
                      Coordinates: {{ clockForm.latitude }}, {{ clockForm.longitude }}
                    </small>
                  </div>
                  <button @click="getCurrentLocation" class="btn btn-sm btn-outline-success">
                    <i class="mdi mdi-refresh"></i>
                  </button>
                </div>
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Notes (Optional)</label>
              <textarea v-model="clockForm.notes" class="form-control" rows="3" placeholder="Any notes about your shift..."></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
            <button type="button" @click="confirmClockAction" class="btn"
                    :class="clockAction === 'IN' ? 'btn-success' : 'btn-danger'"
                    :disabled="processing || gettingLocation || !clockForm.address">
              <span v-if="processing">
                <span class="spinner-border spinner-border-sm me-1"></span>
                Processing...
              </span>
              <span v-else>
                <i class="mdi me-1" :class="clockAction === 'IN' ? 'mdi-clock-in' : 'mdi-clock-out'"></i>
                Confirm {{ clockAction === 'IN' ? 'Clock In' : 'Clock Out' }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation";
import { authUrl, execute } from "@/api";

export default {
  name: "EmployeeAttendanceClock",
  components: { NavigationBar },

  data() {
    return {
      loading: false,
      processing: false,
      gettingLocation: false,
      locationError: null,
      clockData: null,
      currentTime: '',
      currentDate: '',
      clockAction: 'IN',
      clockForm: {
        latitude: null,
        longitude: null,
        address: '',
        notes: ''
      },
      clockActionModalInstance: null,
      timeInterval: null,
      workingDurationInterval: null,
      workingDuration: '00:00:00'
    };
  },

  mounted() {
    this.loadClockStatus();
    this.initClockActionModal();
    this.startClock();
  },

  beforeUnmount() {
    if (this.timeInterval) {
      clearInterval(this.timeInterval);
    }
    if (this.workingDurationInterval) {
      clearInterval(this.workingDurationInterval);
    }
  },

  methods: {
    async loadClockStatus() {
      this.loading = true;

      try {
        const data = new FormData();
        data.append("function", "getEmployeeClockStatus");

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.clockData = res.data.data;
          this.currentTime = res.data.data.currentTime;
          this.currentDate = res.data.data.currentDate;

          // Start working duration counter if clocked in
          if (this.clockData.clockStatus.isClockedIn) {
            this.startWorkingDurationCounter();
          }
        } else {
          throw new Error(res.data?.message || 'Failed to load clock status');
        }
      } catch (error) {
        this.$toast?.error("Failed to load clock status: " + error.message) ||
        console.error("Failed to load clock status:", error);
      } finally {
        this.loading = false;
      }
    },

    startClock() {
      // Update current time every second
      this.timeInterval = setInterval(() => {
        const now = new Date();
        this.currentTime = now.toLocaleTimeString('en-US', {
          hour12: false,
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      }, 1000);
    },

    startWorkingDurationCounter() {
      if (this.workingDurationInterval) {
        clearInterval(this.workingDurationInterval);
      }

      this.updateWorkingDuration();

      this.workingDurationInterval = setInterval(() => {
        this.updateWorkingDuration();
      }, 1000);
    },

    updateWorkingDuration() {
      if (!this.clockData || !this.clockData.clockStatus.checkInTime) return;

      const checkIn = new Date(this.clockData.clockStatus.checkInTime);
      const now = new Date();
      const diff = now - checkIn;

      const hours = Math.floor(diff / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);

      this.workingDuration = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    },

    initClockActionModal() {
      const modalElement = this.$refs.clockActionModal;
      if (modalElement && typeof bootstrap !== 'undefined') {
        this.clockActionModalInstance = new bootstrap.Modal(modalElement);
      }
    },

    openClockModal(action) {
      this.clockAction = action;
      this.clockForm.latitude = null;
      this.clockForm.longitude = null;
      this.clockForm.address = '';
      this.clockForm.notes = '';
      this.locationError = null;

      if (this.clockActionModalInstance) {
        this.clockActionModalInstance.show();
      }

      // Automatically get location when modal opens
      this.getCurrentLocation();
    },

    async getCurrentLocation() {
      this.gettingLocation = true;
      this.locationError = null;

      if (!navigator.geolocation) {
        this.locationError = 'Geolocation is not supported by your browser';
        this.gettingLocation = false;
        return;
      }

      navigator.geolocation.getCurrentPosition(
          async (position) => {
            this.clockForm.latitude = position.coords.latitude;
            this.clockForm.longitude = position.coords.longitude;

            // Get address from coordinates using reverse geocoding
            await this.getAddressFromCoordinates(
                position.coords.latitude,
                position.coords.longitude
            );

            this.gettingLocation = false;
          },
          (error) => {
            this.gettingLocation = false;

            switch(error.code) {
              case error.PERMISSION_DENIED:
                this.locationError = 'Location access denied. Please enable location permissions in your browser settings.';
                break;
              case error.POSITION_UNAVAILABLE:
                this.locationError = 'Location information is unavailable.';
                break;
              case error.TIMEOUT:
                this.locationError = 'Location request timed out. Please try again.';
                break;
              default:
                this.locationError = 'An unknown error occurred while getting location.';
            }
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
          }
      );
    },

    async getAddressFromCoordinates(latitude, longitude) {
      try {
        // Using OpenStreetMap's Nominatim API (free, no API key required)
        const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
            {
              headers: {
                'Accept': 'application/json'
              }
            }
        );

        if (response.ok) {
          const data = await response.json();

          // Build a readable address
          const address = data.address;
          let formattedAddress = [];

          if (address.road) formattedAddress.push(address.road);
          if (address.suburb) formattedAddress.push(address.suburb);
          if (address.city) formattedAddress.push(address.city);
          if (address.county && !address.city) formattedAddress.push(address.county);
          if (address.country) formattedAddress.push(address.country);

          this.clockForm.address = formattedAddress.join(', ') || data.display_name;
        } else {
          // Fallback to coordinates if reverse geocoding fails
          this.clockForm.address = `Location: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
        }
      } catch (error) {
        console.error('Error getting address:', error);
        // Fallback to coordinates
        this.clockForm.address = `Location: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
      }
    },

    async confirmClockAction() {
      if (!this.clockForm.latitude || !this.clockForm.longitude || !this.clockForm.address) {
        this.$toast?.error("Location is required. Please allow location access.") ||
        alert("Location is required. Please allow location access.");
        return;
      }

      this.processing = true;

      try {
        const data = new FormData();
        data.append("function", "employeeClockAction");
        data.append("action", this.clockAction);
        data.append("latitude", this.clockForm.latitude);
        data.append("longitude", this.clockForm.longitude);
        data.append("address", this.clockForm.address);
        data.append("notes", this.clockForm.notes);

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.$toast?.success(res.data.message) ||
          alert(res.data.message);

          if (this.clockActionModalInstance) {
            this.clockActionModalInstance.hide();
          }

          // Reload clock status
          await this.loadClockStatus();
        } else {
          throw new Error(res.data?.message || 'Failed to record attendance');
        }
      } catch (error) {
        this.$toast?.error("Error: " + error.message) ||
        alert("Error: " + error.message);
      } finally {
        this.processing = false;
      }
    },

    formatDistance(meters) {
      if (!meters) return 'N/A';
      if (meters < 1000) {
        return Math.round(meters) + 'm';
      } else {
        return (meters / 1000).toFixed(1) + 'km';
      }
    },

    formatTime(datetime) {
      if (!datetime) return 'N/A';
      const d = new Date(datetime);
      return d.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    },

    formatScheduleTime(time) {
      if (!time) return 'N/A';
      const [hours, minutes] = time.split(':');
      const date = new Date();
      date.setHours(parseInt(hours), parseInt(minutes));
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    },

    formatDate(date) {
      if (!date) return 'N/A';
      const d = new Date(date);
      const options = { month: 'short', day: 'numeric' };
      return d.toLocaleDateString('en-US', options);
    },

    formatDateLong(date) {
      if (!date) return 'N/A';
      const d = new Date(date);
      const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
      return d.toLocaleDateString('en-US', options);
    },

    getStatusBadgeClass(status) {
      const statusLower = status?.toLowerCase();
      if (statusLower === 'present') return 'badge bg-success';
      if (statusLower === 'late') return 'badge bg-warning';
      if (statusLower === 'partial') return 'badge bg-secondary';
      if (statusLower === 'absent') return 'badge bg-danger';
      if (statusLower === 'overtime') return 'badge bg-info';
      return 'badge bg-secondary';
    },

    gotTo(route) {
      sessionStorage.setItem("selectedSub", route);
      sessionStorage.setItem("currentPage", route);
      this.$router.push(route);
    }
  }
};
</script>

<style scoped>
.avatar-xl {
  width: 96px;
  height: 96px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.avatar-lg {
  width: 72px;
  height: 72px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.avatar-title {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.bg-success-subtle {
  background-color: rgba(40, 167, 69, 0.1);
}

.bg-danger-subtle {
  background-color: rgba(220, 53, 69, 0.1);
}

.badge-soft-light {
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
}

/* Timeline Styles */
.timeline {
  list-style: none;
  padding: 20px 0;
  position: relative;
}

.timeline:before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #dee2e6;
  left: 30px;
}

.timeline-item {
  margin-bottom: 20px;
  position: relative;
  padding-left: 60px;
}

.timeline-badge {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  text-align: center;
  position: absolute;
  left: 10px;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
  border: 3px solid white;
  box-shadow: 0 0 0 2px #dee2e6;
}

.timeline-panel {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  border-left: 3px solid #5b73e8;
}

.timeline-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.timeline-body {
  margin-top: 8px;
}

@media (max-width: 768px) {
  .card-body {
    padding: 1rem;
  }

  .display-1 {
    font-size: 3rem;
  }
}
</style>